import { createTheme } from '@mantine/core'

/**
 * App theme.
 *
 * Font stack leads with faces that actually carry Hebrew glyphs. The Vite
 * default (`Inter, system-ui, Avenir, Helvetica, Arica`) has no Hebrew
 * coverage, so every label would fall back to whatever the browser picked —
 * inconsistently, and usually to something that renders Hebrew a size smaller
 * than the surrounding Latin text.
 *
 * Numbers use a tabular figure stack so digits line up in the money columns.
 * A BOM total that shifts by a pixel per row is hard to scan.
 */
export const theme = createTheme({
  fontFamily:
    'Segoe UI, Arial, "Noto Sans Hebrew", "Helvetica Neue", system-ui, sans-serif',
  fontFamilyMonospace:
    'Consolas, "Courier New", ui-monospace, SFMono-Regular, monospace',
  headings: {
    fontFamily:
      'Segoe UI, Arial, "Noto Sans Hebrew", "Helvetica Neue", system-ui, sans-serif',
    fontWeight: '600',
  },
  primaryColor: 'blue',
  defaultRadius: 'md',
  cursorType: 'pointer',
  components: {
    Table: {
      defaultProps: { highlightOnHover: true, striped: false, stickyHeader: true },
    },
  },
})

/**
 * For Latin identifiers inside an RTL document — SKUs, part numbers, PO
 * numbers, order references.
 *
 * Without an explicit direction, bidi reorders any *neutral* character at the
 * edge of the string: `INT-C9200-` renders as `-INT-C9200`, moving the
 * trailing hyphen to the visual left. It looks like a different part number,
 * and for something people read aloud to a supplier that matters. `embed`
 * isolates the run so the identifier reads exactly as stored, while the cell
 * still aligns to the RTL start edge.
 */
export const CODE_STYLE: React.CSSProperties = {
  direction: 'ltr',
  unicodeBidi: 'embed',
  textAlign: 'start',
  whiteSpace: 'nowrap',
}

/** Monospaced, tabular digits — for any cell holding money or a quantity. */
export const NUMERIC_STYLE: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  fontFamily: 'Consolas, "Courier New", monospace',
  // Numbers are written left-to-right even inside an RTL document. Without
  // this, a value like "1,234.56-" can render with the minus on the wrong end.
  direction: 'ltr',
  unicodeBidi: 'embed',
  textAlign: 'end',
  whiteSpace: 'nowrap',
}

/**
 * For a flex-column child that should grow to fill whatever space its
 * siblings leave, rather than sizing to its own content — the "grid fills
 * the rest" half of the low-res layout pass (DP-11).
 *
 * Needs two things to actually work, both easy to forget: the *parent* must
 * itself have a definite height (a flex item with `flex: 1` counts, so does
 * an element with `h="100%"` whose own parent has one — `Shell.tsx`'s
 * `AppShell.Main` is where the chain starts), and the child doing the actual
 * scrolling needs `overflow: auto` plus a height that resolves against *this*
 * box (`height: '100%'`, not another `maxHeight` guess). `minHeight: 0`
 * is not optional: without it a flex item refuses to shrink below its
 * content's natural height — a 6,500-row table's "natural height" is
 * whatever it takes to show all 6,500 rows, which is exactly the fixed-tall-
 * grid-plus-page-scrollbar bug this exists to fix.
 */
export const FILL_REMAINING: React.CSSProperties = { flex: '1 1 0%', minHeight: 0 }

/**
 * `FILL_REMAINING`, plus turning the box itself into a flex column — for the
 * `Paper` that directly wraps a `DataGrid` given `maxHeight="100%"`. The grid
 * needs *two* things from its parent: a share of the page's remaining height
 * (`FILL_REMAINING` alone would give it that) and to itself be a flex
 * container, so the grid's own internal box can resolve `flex: 1` against
 * *it* in turn. One without the other is a grid that either does not grow at
 * all, or grows but never actually caps/scrolls — both look fine empty and
 * both fail the moment a real catalog's worth of rows shows up.
 */
export const FILL_PANEL: React.CSSProperties = {
  ...FILL_REMAINING,
  display: 'flex',
  flexDirection: 'column',
}

/**
 * The low-res pass's one threshold, shared by everything that reacts to it
 * (`useMediaQuery(COMPACT_QUERY)`): `FilterPanel` (collapses its selects),
 * `DataGrid` (drops to dense row spacing) and the pages that trim their own
 * surrounding chrome alongside both (`Catalog.tsx`, `BomList.tsx`).
 *
 * **height ≤ 800 or width < 1200** — an OR, not Mantine's usual single named
 * breakpoint, because a short desktop window (1366×768, 1280×720) needs the
 * same compacting a narrow phone does, and neither axis alone would catch it.
 * `matchMedia` (what `useMediaQuery` calls) evaluates a comma-separated query
 * list as OR natively, so this is one hook per caller, not two combined by
 * hand.
 *
 * `DataGrid`'s own `hideBelow` column-hiding is deliberately *not* on this
 * query — it stays keyed to the plain `sm`/`md`/`lg` width breakpoints
 * (`useMatches`), because *which* columns a grid can afford only depends on
 * how much horizontal room there is, never on height.
 */
export const COMPACT_QUERY = '(max-height: 800px), (max-width: 1199px)'
